import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { useEffect } from 'react';

// Layouts
import MainLayout from './components/layout/MainLayout';
import AuthLayout from './components/layout/AuthLayout';

// Components
import PrivateRoute from './components/common/PrivateRoute';
import SelectionDialog from './components/common/SelectionDialog';

// Pages
import LoginPage from './pages/Login/LoginPage';
import DashboardPage from './pages/Panel/DashboardPage';
import POSPage from './pages/POS/POSPage';
import BillingPage from './pages/Billing/BillingPage';
import SettingsPage from './pages/Settings/SettingsPage';

// Placeholder pages
const ComingSoonPage = () => (
  <div style={{ padding: '20px', textAlign: 'center' }}>
    <h2>Página en desarrollo</h2>
    <p>Esta funcionalidad estará disponible pronto.</p>
  </div>
);

// Componente para proteger rutas de módulos específicos
const ModuleRouteGuard = ({ children, requiredModule }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Verificar autorización inmediatamente
  const getAuthorizedModules = () => {
    try {
      const stored = localStorage.getItem('nx2026_authorized_modules');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  };
  
  const authorizedModules = getAuthorizedModules();
  
  // Si el usuario no tiene autorización para este módulo, redirigir inmediatamente
  useEffect(() => {
    if (requiredModule && !authorizedModules.includes(requiredModule)) {
      console.log(`[ModuleRouteGuard] Acceso denegado a ${location.pathname}. Módulo requerido: ${requiredModule}, Autorizados: ${authorizedModules.join(', ')}`);
      navigate('/billing', { replace: true });
    }
  }, []);
  
  // Si no tiene acceso, no renderizar nada (redirect happening)
  if (requiredModule && !authorizedModules.includes(requiredModule)) {
    return null;
  }
  
  return children;
};

// Page wrapper que muestra el SelectionDialog
const AppContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading } = useAuth();

  // Si está cargando, mostrar indicador
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: '20px' }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '5px solid #f3f3f3',
          borderTop: '5px solid #3498db',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
        <span style={{ color: '#666', fontSize: '16px' }}>Cargando...</span>
      </div>
    );
  }

  // Si no está autenticado, no Renderizar nada (PrivateRoute se encarga)
  if (!isAuthenticated) {
    return null;
  }

  // Escuchar evento de modo seleccionado automáticamente para navegar
  useEffect(() => {
    const handleModoSeleccionado = (event) => {
      if (event.detail && event.detail.modo) {
        // Navegar al módulo correspondiente
        if (event.detail.modo === 'POS') {
          navigate('/pos', { replace: true });
        } else if (event.detail.modo === 'FACTURACION') {
          navigate('/billing', { replace: true });
        }
      }
    };

    window.addEventListener('nx2026-modo-seleccionado', handleModoSeleccionado);
    return () => window.removeEventListener('nx2026-modo-seleccionado', handleModoSeleccionado);
  }, [navigate]);

  return (
    <>
      {/* Diálogo de selección de modo (bloqueante) */}
      <SelectionDialog />
      
      {/* Rutas con Layout */}
      <Routes>
        <Route element={<MainLayout />}>
          {/* Ruta principal - redirige según el modo */}
          <Route path="/" element={<Navigate to="/billing" replace />} />
          
          {/* Módulo POS - solo accesible si tiene autorización */}
          <Route path="/pos" element={
            <ModuleRouteGuard requiredModule="POS">
              <POSPage />
            </ModuleRouteGuard>
          } />
          <Route path="/pos/*" element={
            <ModuleRouteGuard requiredModule="POS">
              <POSPage />
            </ModuleRouteGuard>
          } />
          
          {/* Módulo Facturación - solo accesible si tiene autorización */}
          <Route path="/billing" element={
            <ModuleRouteGuard requiredModule="FACTURACION">
              <BillingPage />
            </ModuleRouteGuard>
          } />
          <Route path="/billing/:section" element={
            <ModuleRouteGuard requiredModule="FACTURACION">
              <BillingPage />
            </ModuleRouteGuard>
          } />
          
          {/* Rutas adicionales */}
          <Route path="/inventory" element={<ComingSoonPage />} />
          <Route path="/customers" element={<ComingSoonPage />} />
          <Route path="/suppliers" element={<ComingSoonPage />} />
          <Route path="/stock" element={<ComingSoonPage />} />
          <Route path="/quotes" element={<ComingSoonPage />} />
          <Route path="/employees" element={<ComingSoonPage />} />
          <Route path="/statistics" element={<ComingSoonPage />} />
          <Route path="/expenses" element={<ComingSoonPage />} />
          <Route path="/reports" element={<ComingSoonPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/branches" element={<ComingSoonPage />} />
          
          {/* Ruta 404 */}
          <Route path="*" element={<Navigate to="/billing" replace />} />
        </Route>
      </Routes>
    </>
  );
};

function App() {
  return (
    <Routes>
      {/* Rutas públicas */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
      </Route>

      {/* Rutas protegidas - contenido principal de la app */}
      <Route path="/*" element={
        <PrivateRoute>
          <AppContent />
        </PrivateRoute>
      } />
    </Routes>
  );
}

export default App;
